import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  Download, 
  Trash2, 
  Calendar, 
  Shield, 
  Eye,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import ApiService from '../../services/api';

const DocumentManager = ({ userId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/documents`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setDocuments(data.documents || []);
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      setError('Error loading documents: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchDocuments();
    }
  }, [userId, fetchDocuments]);

  const handleDownload = async (documentId, documentName) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/documents/${documentId}/download`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Download failed';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      a.download = `${documentName}_results.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url2);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error downloading document: ' + err.message);
    }
  };

  const handleDownloadOriginal = async (documentId, documentName) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/documents/${documentId}/download-original`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Download failed';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      a.download = documentName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url2);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error downloading original document: ' + err.message);
    }
  };

  const handleDelete = async (documentId) => {
    try {
      setDeletingId(documentId);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/documents/${documentId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDocuments(documents.filter(doc => doc.id !== documentId));
        if (selectedDocument?.id === documentId) {
          setSelectedDocument(null);
          setShowDetails(false);
        }
        
        // Emit event to update document counter
        window.dispatchEvent(new CustomEvent('documentUploaded'));
      } else {
        throw new Error(data.error || 'Delete failed');
      }
    } catch (err) {
      setError('Error deleting document: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewDetails = async (documentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/documents/${documentId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedDocument(data.document);
        setShowDetails(true);
      } else {
        throw new Error('Failed to load document details');
      }
    } catch (err) {
      setError('Error loading document details: ' + err.message);
    }
  };

  const getStatusIcon = (status, hasErrors, aiProcessed) => {
    if (hasErrors) return <AlertCircle className="w-4 h-4 text-red-500" />;
    if (status === 'processed' && aiProcessed) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'stored' && !aiProcessed) return <FileText className="w-4 h-4 text-blue-500" />;
    return <Clock className="w-4 h-4 text-yellow-500" />;
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-lg text-gray-600">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Documents</h1>
        <Button
          onClick={fetchDocuments}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert className="mb-6 border-red-200 bg-red-50">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {documents.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents uploaded yet</h3>
            <p className="text-gray-600">Upload your first military document to get started with AI processing.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc) => (
            <Card key={doc.id} className="border border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(doc.status, doc.has_errors, doc.ai_processed)}
                    <CardTitle className="text-lg truncate">{doc.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {doc.category || doc.type}
                    </Badge>
                    {doc.ai_processed && (
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        AI Processed
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(doc.uploaded_at)}
                    </div>
                    {doc.file_size && (
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {(doc.file_size / 1024).toFixed(1)} KB
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    {doc.ai_processed && doc.confidence && (
                      <Badge className={getConfidenceColor(doc.confidence)}>
                        {doc.confidence} confidence
                      </Badge>
                    )}
                    <span className="text-sm text-gray-600">
                      {doc.ai_processed ? `${doc.benefits_extracted} benefits` : 'Stored only'}
                    </span>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => handleViewDetails(doc.id)}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    
                    <Button
                      onClick={() => handleDownloadOriginal(doc.id, doc.name)}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                    
                    {doc.ai_processed && (
                      <Button
                        onClick={() => handleDownload(doc.id, doc.name)}
                        variant="outline"
                        size="sm"
                        className="flex-1 text-blue-600 hover:text-blue-700"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        AI Results
                      </Button>
                    )}
                    
                    <Button
                      onClick={() => handleDelete(doc.id)}
                      variant="outline"
                      size="sm"
                      disabled={deletingId === doc.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {deletingId === doc.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Document Details Modal */}
      {showDetails && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">{selectedDocument.document_name}</CardTitle>
                <Button
                  onClick={() => setShowDetails(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </Button>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="space-y-6">
                {/* Document Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Document Type</label>
                    <p className="text-gray-900">{selectedDocument.document_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Processing Date</label>
                    <p className="text-gray-900">{formatDate(selectedDocument.processed_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Confidence</label>
                    <Badge className={getConfidenceColor(selectedDocument.confidence)}>
                      {selectedDocument.confidence}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <p className="text-gray-900 capitalize">{selectedDocument.status}</p>
                  </div>
                </div>

                {/* Extracted Data */}
                {selectedDocument.extracted_data && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Extracted Information</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                        {JSON.stringify(selectedDocument.extracted_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => handleDownload(selectedDocument.id, selectedDocument.document_name)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Results
                  </Button>
                  
                  <Button
                    onClick={() => {
                      handleDelete(selectedDocument.id);
                      setShowDetails(false);
                    }}
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Document
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;